import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScheduleSettingsDialog({ open, onClose, onSaved }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/assessments/schedule-settings`).then((r) => setS(r.data)).catch(() => toast.error("Could not load settings"));
  }, [open]);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setRow = (i, k, v) => setS((p) => ({ ...p, readmission_by_insurance: p.readmission_by_insurance.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) }));

  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/assessments/schedule-settings`, {
        supervisory_interval_days: Number(s.supervisory_interval_days), due_soon_days: Number(s.due_soon_days),
        readmission_default_days: Number(s.readmission_default_days),
        restart_recurring_on_readmission: s.restart_recurring_on_readmission !== false,
        readmission_by_insurance: s.readmission_by_insurance.map((x) => ({ insurance_company: x.insurance_company, days: Number(x.days) })),
      });
      toast.success("Assessment schedule saved");
      onSaved?.(r.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="schedule-settings-dialog">
        <DialogHeader>
          <DialogTitle>Assessment schedule</DialogTitle>
          <DialogDescription>How often supervisory visits and re-admissions are due. Re-admission timing can differ per insurance company.</DialogDescription>
        </DialogHeader>
        {!s ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Supervisory every (days)</Label>
                <Input type="number" min={1} value={s.supervisory_interval_days} onChange={(e) => set("supervisory_interval_days", e.target.value)} data-testid="sched-supervisory-days" />
              </div>
              <div>
                <Label className="text-xs">Warn (days before)</Label>
                <Input type="number" min={0} value={s.due_soon_days} onChange={(e) => set("due_soon_days", e.target.value)} data-testid="sched-due-soon-days" />
              </div>
              <div>
                <Label className="text-xs">Re-admission default (days)</Label>
                <Input type="number" min={1} value={s.readmission_default_days} onChange={(e) => set("readmission_default_days", e.target.value)} data-testid="sched-readmission-days" />
                <label className="flex items-center gap-2 text-xs text-slate-700 mt-2">
                  <input type="checkbox" checked={s.restart_recurring_on_readmission !== false} onChange={(e) => set("restart_recurring_on_readmission", e.target.checked)} data-testid="sched-restart-recurring" />
                  When a re-admission is completed, start a fresh Plan of Care cycle automatically (content carried over)
                </label>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Re-admission by insurance company</Label>
                <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => set("readmission_by_insurance", [...s.readmission_by_insurance, { insurance_company: "", days: 180 }])} data-testid="sched-add-insurance">
                  <Plus className="w-3.5 h-3.5" /> Add
                </Button>
              </div>
              {s.readmission_by_insurance.length === 0 && <p className="text-xs text-slate-400">No overrides — every client uses the default.</p>}
              <div className="space-y-2">
                {s.readmission_by_insurance.map((r, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`sched-insurance-row-${i}`}>
                    <Input placeholder="Insurance company (matches client's Insurance Company)" value={r.insurance_company} onChange={(e) => setRow(i, "insurance_company", e.target.value)} data-testid={`sched-insurance-name-${i}`} />
                    <Input type="number" min={1} className="w-24" value={r.days} onChange={(e) => setRow(i, "days", e.target.value)} data-testid={`sched-insurance-days-${i}`} />
                    <span className="text-xs text-slate-500">days</span>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600" onClick={() => set("readmission_by_insurance", s.readmission_by_insurance.filter((_, idx) => idx !== i))} data-testid={`sched-insurance-remove-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !s} className="gap-1" data-testid="sched-save-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
